import React, {
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useFormik } from 'formik';
import CheckboxGroup from 'component/atom/forms/checkbox/CheckboxGroup';
import styles from './FieldForm.module.scss';
import { Agent } from 'hooks/endpoints';

const FieldForm = forwardRef(function FieldForm(
  {
    agentData,
    selectedFields,
    setSelectedFields,
    resetTrigger,
    onSubmitted,
    saveConnections,
    refetchAgentData,
    getCount,
    detailColumns = [],
  },
  ref
) {
  const didMountRef = useRef(false);

  const columnsByColumnId = useMemo(() => {
    const m = new Map();
    (detailColumns || []).forEach((c) => m.set(c.columnId, c));
    return m;
  }, [detailColumns]);

  const options = useMemo(() => {
    return (agentData || []).map((field) => ({
      label: field.displayName,
      value: field.fieldName,
      description: field.columnDescription,
    }));
  }, [agentData]);

  const initialFromDetail = useMemo(() => {
    if (!detailColumns?.length) return null;
    const selectedColumnIds = new Set(
      detailColumns.filter((c) => c.isChecked).map((c) => c.columnId)
    );
    return (agentData || [])
      .filter((f) => selectedColumnIds.has(f.id))
      .map((f) => f.fieldName);
  }, [detailColumns, agentData]);

  const fallbackFromAgent = useMemo(() => {
    return (agentData || []).filter((f) => f.isChecked).map((f) => f.fieldName);
  }, [agentData]);

  const initialFields =
    selectedFields ?? initialFromDetail ?? fallbackFromAgent;

  const formik = useFormik({
    initialValues: { fields: initialFields },
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const chosen = new Set(values.fields || []);
      const payload = (agentData || []).map((f) => {
        const dc = columnsByColumnId.get(f.id);
        return {
          id: dc?.id, // <-- use the agentConnectionDetailColumns.id
          isChecked: chosen.has(f.fieldName),
        };
      });
      saveConnections(
        {
          url: Agent.SaveInnerConnectionDetails,
          data: payload,
          requireAuth: true,
        },
        {
          onSuccess: () => {
            setSubmitting(false);
            refetchAgentData();
            if (typeof onSubmitted === 'function') onSubmitted();
          },
          onError: () => {
            setSubmitting(false);
          },
        }
      );
    },
  });

  useImperativeHandle(ref, () => ({
    submit: () => formik.handleSubmit(),
  }));

  useEffect(() => {
    if (!didMountRef.current) {
      if (!selectedFields || !selectedFields.length) {
        getCount(initialFields || []);
      }
      didMountRef.current = true;
    }
  }, [initialFields, selectedFields, getCount]);

  useEffect(() => {
    // keep resetTrigger behavior intact
  }, [resetTrigger]);

  return (
    <form onSubmit={formik.handleSubmit} className={styles['field-form']}>
      <CheckboxGroup
        name='fields'
        options={options}
        selectedValues={formik.values.fields}
        onChange={(newValues) => {
          formik.setFieldValue('fields', newValues);
          setSelectedFields(newValues);
        }}
        error={formik.errors.fields}
        className={styles['checkbox-container']}
        column
        hasTag
        separators
        labelClassName={styles['checkbox-label']}
      />
    </form>
  );
});

export default FieldForm;
